import { useEffect } from "react";
import Login from './Login';
import LoginClient from './Login_client';
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import useAuthStore from "./states/authStore";
import useLookupStore from "./CRM/states/useLookupStore";
import useDealStore from "./CRM/states/useDealStore";
import useAppDataStore from "./states/useAppDataStore";
import entranceAnimation from "./assets/IQVentory Entrance Animation.json";
import Main from './Main'; // main of the whole ERP
import Admin_Main from './Admin/Main';
import IT_Main from './IT/Main';
import CRM_main from './CRM/Main';
import PM_tool_Main from './PM_tool/Main';
import PaymentMain from "./Payment/Main";
import { PageTransitionProvider } from "@iq/models";


function ProtectedRoute({ children, authenticated }) {
  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  const navigate = useNavigate();
  const authenticated = useAuthStore((state) => state.authenticated);
  const profileId = useAuthStore((state) => state.profile?.id ?? null);
  const initialized = useAuthStore((state) => state.initialized);
  const bootstrapSession = useAuthStore((state) => state.bootstrapSession);
  const revalidateSession = useAuthStore((state) => state.revalidateSession);
  const hydrateLookups = useLookupStore((state) => state.hydrateLookups);
  const resetLookups = useLookupStore((state) => state.resetLookups);
  const resetDeals = useDealStore((state) => state.resetDeals);
  const hydrateAppData = useAppDataStore((state) => state.hydrateAppData);
  const resetAppData = useAppDataStore((state) => state.resetAppData);

  useEffect(() => {
    bootstrapSession();
  }, [bootstrapSession]);

  useEffect(() => {
    if (!initialized) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      revalidateSession();
    }, 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [initialized, revalidateSession]);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    if (!authenticated) {
      resetLookups();
      resetDeals();
      return;
    }

    // Ensure stores refresh when the signed-in profile changes.
    resetLookups();
    resetDeals();
    hydrateLookups();
  }, [initialized, authenticated, profileId, hydrateLookups, resetLookups, resetDeals]);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    if (!authenticated || !profileId) {
      resetAppData();
      return;
    }

    // Hydrate shared app lookups (products/orgs) for all routes, not only Main page.
    resetAppData();
    hydrateAppData({ id: profileId });
  }, [initialized, authenticated, profileId, hydrateAppData, resetAppData]);

  if (!initialized) {
    return <div className="App"><div className="Main-display">Loading session...</div></div>;
  }

  return (
    <div className="App">
      <div className="Main-display">
        <PageTransitionProvider
          animationData={entranceAnimation}
          navigate={navigate}
          enterDurationMs={200}
          animationDurationMs={1500}
          exitDurationMs={300}
        >
          <Routes>
            <Route path="/login" element={authenticated ? <Navigate to="/" replace /> : <Login />}/>
            <Route path="/login-client" element={authenticated ? <Navigate to="/" replace /> : <LoginClient />}/>

            <Route path="/" element={<ProtectedRoute authenticated={authenticated}><Main /></ProtectedRoute>}/>

            <Route path="/IT" element={<ProtectedRoute authenticated={authenticated}><IT_Main /></ProtectedRoute>}/>

            {/* only supreme role gets to see this eventually */}
            <Route path="/admin" element={<ProtectedRoute authenticated={authenticated}><Admin_Main /></ProtectedRoute>}/>

            <Route path="/contacts" element={<ProtectedRoute authenticated={authenticated}><CRM_main /></ProtectedRoute>}/>

            <Route path="/projects" element={<ProtectedRoute authenticated={authenticated}><PM_tool_Main /></ProtectedRoute>}/>

            <Route path="/payment" element={<ProtectedRoute authenticated={authenticated}><PaymentMain /></ProtectedRoute>}/> 
            
            <Route path="*" element={<Navigate to={authenticated ? "/" : "/login"} replace />}/>

          </Routes>
        </PageTransitionProvider>
      </div>
    </div>
  );
}

export default App;